import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { API_URL, calculerHeuresBudgetees, calculerCoutEquipe, getCoutEmployeur } from './utils';
import { CoutEmployeurBadge } from './CoutEmployeurBadge';  // 🚀 [Phase 107.B — 2026-07-14]
import { useBtEquipeMatrice } from '@/hooks/useBtEquipeMatrice';  // 🧱 Refactor BT Phase 3
import { useBtEditerEffets } from '@/hooks/useBtEditerEffets';  // 🧱 Refactor BT Phase 3 — T1
import { useBtEditerHandlers } from '@/hooks/useBtEditerHandlers';
import { useBtEditerSauvegarde } from '@/hooks/useBtEditerSauvegarde';
import { formInitialEditer } from '@/lib/btFormInitialEditer';
import { SousDialoguesEditer } from './editer/dialogs/SousDialoguesEditer';
import {
  SectionClientDossier,
  SectionDescriptionNotes,
  SectionEquipeHeures,
  SectionHeureDebutPluie,
  SectionDatesChantier,
  SectionLavagePression,
  SectionEquipementsLocation,
  SectionProduits,
  SectionEtapes,
  SectionZones,
} from './editer/sections';  // 🧱 Refactor BT Phase 3 — Étape 2
// 🛡️ [Phase 76 — 2026-06-21] Helpers purs Jest-testables pour la construction
// du payload de save. Voir __tests__/btSavePayloadHelpers.phase76.test.js (17 tests).
// Garantit que `dates_assignees=[]` (décochage volontaire utilisatrice) n'est
// JAMAIS écrasé par un fallback `dates_chantier`.
import { construirePayloadEditer } from '@/lib/btPayload';  // 🧱 Refactor BT Phase 3
import { getCCQInfo } from '@/lib/ccq-holidays';
import { useEstimationWatcher } from '@/hooks/useEstimationWatcher';
import useAutoSave from '@/hooks/useAutoSave';
import { AjusterCalendrierDialog } from './AjusterCalendrierDialog';
import { EditorRiche } from '@/components/crm/EditorRiche';
import { Plus, ClipboardList, Home, TreePine, Trash2, Package, User, MapPin, ChevronDown, ChevronUp, Upload, Image as ImageIcon, CheckCircle, X, Calendar, ChevronLeft, ChevronRight, Clock, LogIn, LogOut as LogOutIcon, Edit, Edit3, Phone, Mail, Building2, Printer, ShoppingCart, History, Send, Wrench, AlertTriangle, AlertCircle, Users, Play, Square, Coffee, Check, MoreVertical, RefreshCw, Cloud, CheckCircle2, MessageSquare, FileText, UploadCloud, Download, Lock, Sparkles } from 'lucide-react';
import { MiniCalendar } from './MiniCalendar';
// 🗑️ [Phase 51 cleanup 2026-06-08] CollisionChecker retiré du formulaire BT
// (conflits d'horaire désormais gérés uniquement depuis le calendrier).
import { ProductSearchCombobox } from './ProductSearchCombobox';
import { ToursEchafaudsConfig } from './ToursEchafaudsConfig';
import { ListeChargementDialog } from '@/components/echafauds/ListeChargementDialog';
import { filterEmployeesForBT, normalizeEmployesAssignes } from '@/lib/btEmployees';
// 🆕 [Phase 56 BT — P2.0 refactor] Composants utilitaires extraits dans un fichier dédié
import { IAValidationButton } from './BTSubComponents';
// NOTE : `NacelleSelector` est exporté dans BTSubComponents pour usage futur
// (auto-sélection nacelle au lavage), pas utilisé actuellement dans ce dialog.
// 🆕 [Phase 56 BT — P2.2 refactor] Sub-dialogs extraits
import { LeaveConflictDialog, ZonePhotoFullscreenDialog, LeaveConflictWarningInline } from './BTSubDialogs';
// 🆕 [SESSION MAX SUITE — P2] BTFormContext foundation pour extractions futures
import { BTFormProvider } from './BTFormContext';
// 🐛 [Phase v13.48 — 2026-07-14] Section UI Planification approximative (Sujet 4.B)
import { PlanificationApproxSection, persistPlanificationApprox } from './PlanificationApproxSection';


export function EditerBonTravailDialog({ open, onClose, dossier, bon, produits, employees, equipements, onSaved }) {
  // 🛡️ [v13.65.4 — 2026-02-28] DURCISSEMENT DÉFENSIF — normalise en LECTURE
  //   les variantes legacy des `employes_assignes` (employe_id → employee_id,
  //   dates → dates_assignees, etc.). Audit DB réelle 2026-02-28 : 0 doc en
  //   état non canonique — mais cette normalisation blinde contre les imports,
  //   seeds tiers, agents automatiques ou futures régressions qui pourraient
  //   introduire ces variantes. Sans ça, le matching de préservation
  //   (originalAssignesRef, membresExtras) échoue et l'entrée est larguée
  //   silencieusement au save.
  //   ⚠️ On REMPLACE `bon.employes_assignes` par la version normalisée pour
  //   que TOUTES les lectures downstream (init form, init membresExtras,
  //   init originalAssignesRef, matrice UI) partagent la même source
  //   canonique. Le `bon` d'origine n'est pas muté (spread).
  bon = {
    ...bon,
    employes_assignes: normalizeEmployesAssignes(bon.employes_assignes),
  };

  // 🔄 Phase A2.2 (2026-05-04) — Item #4c : sous-traitants/installateurs filtrés
  // (cohérent avec CreerBonTravailDialog). Les BT historiques ayant déjà des
  // sous-traitants assignés conservent les entrées dans `bon.employes_assignes`
  // (pas de modification destructrice), mais ils ne peuvent plus être ajoutés.
  const employesOriginal = employees || [];
  employees = filterEmployeesForBT(employesOriginal);

  // 🛡️ [v13.65 — 2026-02-28] SAVE NON-DESTRUCTIF (Cause #2 du RCA v13.65).
  //
  //   PIÈGE ÉVITÉ (fix v13.65.1 — 2026-02-28) :
  //     L'implémentation initiale utilisait `useRef` capturant AU MOUNT. Si
  //     `employees` (props) arrivait vide au premier render (chargement async
  //     du parent), le ref contenait TOUS les employes_assignes (l'expression
  //     `!employees.some()` retournait true pour tous). Résultat : le save
  //     écrasait silencieusement toute modification UI ultérieure par le
  //     snapshot mount stale. Symptôme reproduit par le testeur : coche sur
  //     un peintre visible → save → DB inchangée.
  //
  //   NOUVELLE APPROCHE :
  //     Le snapshot des `bon.employes_assignes` originaux est capturé (ref
  //     immutable au mount de la valeur DB), mais la sélection des
  //     « employés à préserver » (invisibles dans le picker) est calculée
  //     DYNAMIQUEMENT au moment du save, avec la valeur COURANTE de
  //     `employees` filtré. Cela garantit que si le parent charge tardivement
  //     `employees`, la logique s'aligne correctement.
  //
  //   RÈGLE DE FUSION STRICTE (utilisatrice — 2026-02-28) :
  //     • Les sélections UI font FOI pour tout employé visible dans le picker.
  //     • `preservedAssignes` ne sert QU'À ré-ajouter les employés absents du
  //       picker (installateur / sous_traitant post-fusion, employee_id
  //       orphelin, etc.). JAMAIS à écraser une entrée visible.
  //     • Concrètement : on ne push qu'un ID absent de `employesAssignesPayload`
  //       (déjà le cas) ET absent de `employees` filtré (garantie stricte).
  const originalAssignesRef = useRef(bon.employes_assignes || []);

  const { isProprietaire, user } = useAuth();
  const { canDoBonTravailAction } = usePermissions() || {};
  const [saving, setSaving] = useState(false);
  const [uploadingZone, setUploadingZone] = useState(null);
  // 🆕 2026-05-08 — Photo de zone en plein écran (lightbox) depuis le formulaire de modification
  const [zonePhotoFullscreen, setZonePhotoFullscreen] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [leaveConflicts, setLeaveConflicts] = useState([]);
  const [equipeOccupee, setEquipeOccupee] = useState({});
  const [congesEquipe, setCongesEquipe] = useState({});
  // 🐛 [Phase v13.48 — 2026-07-14] State planification approximative (Sujet 4.B).
  //   Initialisé depuis `bon.planification_approx` pour la rétrocompat.
  //   Le ref `initialPlanifApprox` sert à savoir si un DELETE est requis
  //   en fin de save (état passé de non-null → null).
  const initialPlanifApproxRef = useRef(bon?.planification_approx || null);
  const [planificationApprox, setPlanificationApprox] = useState(bon?.planification_approx || null);
  const [showAddExtraMembre, setShowAddExtraMembre] = useState(false);

  // Charger les infos du client
  useEffect(() => {
    if (dossier?.client_id) {
      axios.get(`${API_URL}/api/crm/clients/${dossier.client_id}`)
        .then(res => res.data)
        .then(data => setClientInfo(data))
        .catch(() => {});
    }
  }, [dossier?.client_id]);

  // Recalculer les heures au chargement si un chef est assigné
  useEffect(() => {
    if (!bon.chef_equipe_id || !employees.length || !dossier) return;
    const montant = getMontantContrat();
    if (montant <= 0) return;
    const equipe = calculerCoutEquipe(bon.chef_equipe_id, employees, bon.dates_chantier?.[0]);
    const calc = calculerHeuresBudgetees(montant, equipe.coutMoyen, equipe.nbMembres, bon.dates_chantier);
    if (calc.heures > 0 && (!form.heures_budgetees || form.heures_budgetees === '0' || form.heures_budgetees === '')) {
      setForm(f => ({ ...f, heures_budgetees: calc.heures.toString() }));
    }
  }, [bon.chef_equipe_id, employees, dossier]); // eslint-disable-line react-hooks/exhaustive-deps


  const type = bon.type_travaux;
  const isExt = type === 'exterieur';
  const emplacementLabel = isExt ? 'Surface' : 'Pièce';

  // 🧱 Refactor BT Phase 3 — T1 : objet initial déplacé tel quel dans lib/btFormInitialEditer.js (ex-L172-258)
  const [form, setForm] = useState(formInitialEditer({ bon }));

  // ✅ Hook pour surveiller les changements d'estimation et proposer ajustements
  const { showAjustementDialog, setShowAjustementDialog, ajustementData, resetWatcher } = useEstimationWatcher(
    form, 
    employees, 
    calculerHeuresBudgetees, 
    calculerCoutEquipe
  );

  const [calMonth, setCalMonth] = useState(() => {
    if (form.dates_chantier.length > 0) {
      return new Date(form.dates_chantier[0] + 'T12:00:00');
    }
    return new Date();
  });
  
  // ✅ ÉTAPE 4 : États pour échafauds
  const [typesEchafauds, setTypesEchafauds] = useState([]);
  const [showListeChargement, setShowListeChargement] = useState(false);
  const [echafaudsDisponibilite, setEchafaudsDisponibilite] = useState(null);
  const [loadingDisponibilite, setLoadingDisponibilite] = useState(false);

  // ✅ NOUVEAU : États pour nacelles et clients (Phases 3-4)
  const [nacellesDisponibles, setNacellesDisponibles] = useState([]);
  const [clientsListe, setClientsListe] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  // ✅ PHASE 1 & 2 : États pour vérification disponibilité
  const [vehiculesLivraison, setVehiculesLivraison] = useState([]);
  const [loadingVehicules, setLoadingVehicules] = useState(false);
  const [alerteVehicules, setAlerteVehicules] = useState(null);
  const [alerteNacelle, setAlerteNacelle] = useState(null);

  // Trouver le montant du contrat (peut venir du dossier ou de la soumission)
  const getMontantContrat = () => {
    // 1. Montant sur le dossier
    const m1 = parseFloat(dossier?.montant_soumission || 0);
    if (m1 > 0) return m1;
    const m2 = parseFloat(dossier?.montant_final || 0);
    if (m2 > 0) return m2;
    // 2. Montant de la soumission acceptée
    const soumissions = dossier?.soumissions || [];
    const acceptee = soumissions.find(s => s.statut === 'acceptee');
    if (acceptee) {
      const m3 = parseFloat(acceptee.montant_final || acceptee.montant_total || 0);
      if (m3 > 0) return m3;
    }
    return 0;
  };

  // ✅ FIX 2026-04-22 : Sync auto du champ "Heures budgétées" avec le calcul dynamique
  // Lorsque la composition d'équipe change (exclusions/extras/chef/dates), on recalcule
  // l'heures_budgetees en temps réel SAUF si l'utilisateur l'a ajusté manuellement
  // (flag heures_budgetees_ajustees_manuellement). Cela garantit que l'input reste
  // toujours la "source de vérité" alignée avec la réalité de l'équipe.
  // 🧱 Refactor BT Phase 3 — logique « matrice de présence » déplacée telle quelle dans hooks/useBtEquipeMatrice.js
  const {
    employesAyantPunche,
    membresExclus, setMembresExclus,
    membresExtras, setMembresExtras,
    extrasRetiresManuellement, setExtrasRetiresManuellement,
    dialogSuppression,
    lockedInfo,
    handleChefChange,
    employesEffectifsIds, membresMatrice,
    toggleEmployeJour, getEmpDates, estVisibleDansListeEquipe,
    ouvrirDialogSuppression, fermerDialogSuppression, setChefDuJour,
  } = useBtEquipeMatrice({ mode: 'editer', bon, form, setForm, employees, employesOriginal, dossier, getMontantContrat });

  // 🧱 Refactor BT Phase 3 — T1 : déplacé tel quel dans hooks/useBtEditerEffets.js (ex-L328-694)
  const { remorqueEchafaudsId, setRemorqueEchafaudsId, toggleDate } = useBtEditerEffets({ bon, employees, employesEffectifsIds, equipements, form, getEmpDates, getMontantContrat, lockedInfo, membresExclus, membresExtras, nacellesDisponibles, setAlerteNacelle, setAlerteVehicules, setClientsListe, setCongesEquipe, setEchafaudsDisponibilite, setEquipeOccupee, setForm, setLeaveConflicts, setLoadingClients, setLoadingDisponibilite, setLoadingVehicules, setNacellesDisponibles, setTypesEchafauds, setVehiculesLivraison, type });


  // 🧱 Refactor BT Phase 3 — T1 : déplacé tel quel dans hooks/useBtEditerHandlers.js (ex-L697-778)
  const { addEquipement, addEtape, addProduit, addZone, handleZonePhotoUpload, removeEquipement, removeEtape, removeProduit, removeZone, toggleEquipDate, updateEtape, updateProduit, updateZone } = useBtEditerHandlers({ equipements, form, produits, setForm, setUploadingZone });

  // 🧱 Refactor BT Phase 3 — T1 : déplacé tel quel dans hooks/useBtEditerSauvegarde.js (ex-L780-928)
  const { autoSaveBtFn, autoSaveStatus, doSave, handleSave, lastSavedAt, setShowConfirmConge, showConfirmConge } = useBtEditerSauvegarde({ bon, employees, employesOriginal, extrasRetiresManuellement, form, initialPlanifApproxRef, isExt, leaveConflicts, lockedInfo, membresExclus, membresExtras, onSaved, originalAssignesRef, planificationApprox, saving, setSaving });
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startDay = firstDay.getDay(); // 0 = Dimanche
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };
  const calDays = getDaysInMonth(calMonth);
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  // 🧱 Refactor BT Phase 3 — Étape 2 : état + handlers exposés aux sections via BTFormProvider (extra)
  const ctxEditer = {
    addEtape,
    addProduit,
    addZone,
    ajustementData,
    alerteNacelle,
    alerteVehicules,
    bon,
    calDays,
    calMonth,
    canDoBonTravailAction,
    clientInfo,
    clientsListe,
    congesEquipe,
    dialogSuppression,
    doSave,
    dossier,
    emplacementLabel,
    employees,
    employesAyantPunche,
    employesEffectifsIds,
    equipeOccupee,
    estVisibleDansListeEquipe,
    fermerDialogSuppression,
    form,
    getEmpDates,
    getMontantContrat,
    handleChefChange,
    handleZonePhotoUpload,
    isExt,
    isProprietaire,
    leaveConflicts,
    loadingClients,
    loadingVehicules,
    lockedInfo,
    membresExclus,
    membresExtras,
    membresMatrice,
    monthNames,
    nacellesDisponibles,
    onClose,
    open,
    ouvrirDialogSuppression,
    produits,
    remorqueEchafaudsId,
    removeEtape,
    removeProduit,
    removeZone,
    setCalMonth,
    setChefDuJour,
    setExtrasRetiresManuellement,
    setForm,
    setMembresExclus,
    setMembresExtras,
    setShowAddExtraMembre,
    setShowAjustementDialog,
    setShowConfirmConge,
    setShowListeChargement,
    setZonePhotoFullscreen,
    showAddExtraMembre,
    showAjustementDialog,
    showConfirmConge,
    showListeChargement,
    toggleDate,
    toggleEmployeJour,
    type,
    typesEchafauds,
    updateEtape,
    updateProduit,
    updateZone,
    uploadingZone,
    user,
    vehiculesLivraison,
    zonePhotoFullscreen,
  };

  return (
    /* 🆕 [SESSION MAX SUITE — P2] BTFormProvider passif (foundation pour extractions futures) */
    <BTFormProvider form={form} setForm={setForm} employees={employees} bonId={bon?.id} extra={ctxEditer}>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier le Bon de Travail {isExt ? 'Extérieur' : 'Intérieur'}
            </span>
            {/* 🆕 [Phase 43 BIS] Indicateur auto-save discret (uniquement en mode édition) */}
            {bon?.id && (
              <span
                className={`text-[11px] font-normal flex items-center gap-1 ${
                  autoSaveStatus === 'saving' ? 'text-blue-600' :
                  autoSaveStatus === 'saved' ? 'text-emerald-600' :
                  autoSaveStatus === 'pending' ? 'text-amber-600' :
                  autoSaveStatus === 'error' ? 'text-red-600' : 'text-slate-400'
                }`}
                data-testid="bt-autosave-indicator"
                title="Sauvegarde automatique (debounce 3 s)"
              >
                {autoSaveStatus === 'saving' && <>💾 Sauvegarde…</>}
                {autoSaveStatus === 'pending' && <>⏳ En attente…</>}
                {autoSaveStatus === 'saved' && lastSavedAt && (
                  <>💾 Sauvegardé à {lastSavedAt.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</>
                )}
                {autoSaveStatus === 'error' && <>⚠️ Hors-ligne — modifs non sauvegardées</>}
                {autoSaveStatus === 'idle' && <>💾 Auto-save</>}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <SectionClientDossier />
          <SectionDescriptionNotes />
          <SectionEquipeHeures />
          <SectionHeureDebutPluie />
          {/* 🐛 [Phase v13.48 — 2026-07-14] Section Planification approximative (Sujet 4.B).
              Se place AU-DESSUS des dates officielles. La persistance est déclenchée
              par `doSave()` (PUT ou DELETE selon l'état). */}
          <PlanificationApproxSection
            bonId={bon?.id || null}
            planificationApprox={planificationApprox}
            onChange={setPlanificationApprox}
            datesChantierOfficielles={form.dates_chantier}
            employees={employees}
          />

          <SectionDatesChantier />
          {/* ⚠️ Avertissement : employé(s) assigné(s) en congé approuvé pendant la période */}
          {/* 🆕 [SESSION MAX SUITE 2 — P2.4] Extrait dans BTSubDialogs.jsx (Bug 4 BIS protégé) */}
          <LeaveConflictWarningInline leaveConflicts={leaveConflicts} />

          <SectionLavagePression />
          <SectionEquipementsLocation />
          <SectionProduits />
          <SectionEtapes />
          <SectionZones />
          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
            </Button>
          </div>
        </div>
      </DialogContent>

      <SousDialoguesEditer />
    </Dialog>
    </BTFormProvider>
  );
}
